import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { SurveyRespondentService } from '../../../api/services/survey-respondent.service';
import { AuthService } from '../../../services/auth.service';
import { PaginatedList } from '../../../api/models/paginator/paginated-list';
import { SurveyRespondentQuestionnaireModel } from '../../../api/models/survey-respondent-questionnaire-model';
import { QuestionnaireAnswersDialogData } from '../../../types/questionnaire-answers-dialog-data';
import { QuestionnaireResponseAnswersComponent } from '../../questionnaire/questionnaire-response-answers/questionnaire-response-answers.component';
import { DialogService } from '../../../services/dialog.service';

type RespondentQuestionnaireSortKey =
  'questionnaireName' | 'startedAt' | 'status';

@Component({
  selector: 'app-respondent-questionnaires',
  templateUrl: './respondent-questionnaires.component.html',
  standalone: false,
})
export class RespondentQuestionnairesComponent {
  surveyRespondentGuid?: string;
  surveyRespondentQuestionnairesPaginated?: PaginatedList<SurveyRespondentQuestionnaireModel>;
  sortKey?: RespondentQuestionnaireSortKey;
  sortDirection?: 'asc' | 'desc';
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private route: ActivatedRoute,
    private surveyRespondentService: SurveyRespondentService,
    private authService: AuthService,
    private dialogService: DialogService,
  ) {
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.surveyRespondentGuid = params['guid'];
        this.setQuestionnaires(this.surveyRespondentGuid!, 1);
      });
  }

  setQuestionnaires(surveyRespondentGuid: string, pageNumber: number) {
    this.surveyRespondentService
      .surveyRespondentQuestionnairesGet(
        this.authService.getAuthorisationHeaders(),
        {
          surveyRespondentGuid: surveyRespondentGuid,
          pageNumber: pageNumber,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (paginatedList) => {
          this.surveyRespondentQuestionnairesPaginated =
            this.sortPaginatedQuestionnaires(paginatedList);
        },
      });
  }

  changePage(pageNumber: number) {
    this.setQuestionnaires(this.surveyRespondentGuid!, pageNumber);
  }

  getSortAriaLabel(
    label: string,
    sortKey: RespondentQuestionnaireSortKey,
  ): string {
    if (this.sortKey !== sortKey || !this.sortDirection) {
      return `${label}, not sorted. Activate to sort ascending.`;
    }

    if (this.sortDirection === 'asc') {
      return `${label}, sorted ascending. Activate to sort descending.`;
    }

    return `${label}, sorted descending. Activate to clear sorting.`;
  }

  sortResults(sortKey: RespondentQuestionnaireSortKey) {
    // Detail endpoints return paginated pages without sort params, so sort the loaded page only.
    if (this.sortKey === sortKey) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else {
        this.sortDirection = undefined;
        this.sortKey = undefined;
      }
    } else {
      this.sortKey = sortKey;
      this.sortDirection = 'asc';
    }

    if (!this.sortDirection && this.surveyRespondentGuid) {
      this.setQuestionnaires(
        this.surveyRespondentGuid,
        this.surveyRespondentQuestionnairesPaginated?.pageNumber ?? 1,
      );
      return;
    }

    if (this.surveyRespondentQuestionnairesPaginated) {
      this.surveyRespondentQuestionnairesPaginated =
        this.sortPaginatedQuestionnaires(
          this.surveyRespondentQuestionnairesPaginated,
        );
    }
  }

  /**
   * Displays the questionnaire details modal for a paricular instance
   */
  showQuestionnaireDetails(questionnaireInstanceGuid: string) {
    const summary =
      this.surveyRespondentQuestionnairesPaginated!.records.filter(
        (r) => r.questionnaireInstanceGuid == questionnaireInstanceGuid,
      )[0];

    const dialogData: QuestionnaireAnswersDialogData = {
      index:
        this.surveyRespondentQuestionnairesPaginated!.records.length -
        this.surveyRespondentQuestionnairesPaginated!.records.indexOf(summary),
      status: summary.status,
      questionnaireInstanceGuid: summary.questionnaireInstanceGuid,
      startedAt: summary.startedAt,
      timeTaken: '',
      inviteLink: '',
      isTest: summary.isTest,
      surveyRespondentGuid: summary.surveyRespondentGuid,
      surveyRespondentName: summary.surveyRespondentName,
      parentQuestionnaireGuid: summary.parentQuestionnaireGuid,
      parentQuestionnaireInstanceGuid: summary.parentQuestionnaireInstanceGuid,
      parentQuestionnaireName: summary.parentQuestionnaireName,
      invitedByUserGuid: summary.invitedByUserGuid,
      invitedByUserName: summary.invitedByUserName,
      invitedByUserEmail: summary.invitedByUserEmail,
      invitedAt: summary.invitedAt,
    };

    this.dialogService.openDialog(
      QuestionnaireResponseAnswersComponent,
      dialogData,
      789,
      true,
    );
  }

  private sortPaginatedQuestionnaires(
    paginatedList: PaginatedList<SurveyRespondentQuestionnaireModel>,
  ): PaginatedList<SurveyRespondentQuestionnaireModel> {
    if (!this.sortKey || !this.sortDirection) {
      return paginatedList;
    }

    const dir = this.sortDirection === 'asc' ? 1 : -1;
    return {
      ...paginatedList,
      records: [...paginatedList.records].sort((a, b) => {
        const aValue = this.getSortValue(a, this.sortKey!);
        const bValue = this.getSortValue(b, this.sortKey!);
        return aValue.localeCompare(bValue) * dir;
      }),
    };
  }

  private getSortValue(
    questionnaire: SurveyRespondentQuestionnaireModel,
    sortKey: RespondentQuestionnaireSortKey,
  ): string {
    switch (sortKey) {
      case 'questionnaireName':
        return questionnaire.questionnaireName?.toLowerCase() ?? '';
      case 'startedAt':
        return questionnaire.startedAt ?? '';
      case 'status':
        return questionnaire.status?.toLowerCase() ?? '';
      default:
        return '';
    }
  }
}
